import { useSyncExternalStore } from 'react';
import { v2Api } from './api/v2Client';

/**
 * Catalogue des sports (GET /Sport, JWT requis, géré dans ERP → Configuration → Sports). Chargé à l'ouverture de l'ERP
 * (ErpShell, utilisateur connecté), rechargé
 * après chaque modification (`loadSports({ force: true })`). Repli squash/padel (icône et couleur du catalogue, Scripts/SeedSportCatalog.sql) tant qu'il n'est pas reçu.
 * Composants : `useSports()` ; code non-React : `getSports()`.
 */
const FALLBACK = [
  {
    id: null,
    code: 'squash',
    name: 'Squash',
    icon: 'squash-ball',
    color: '#e4a11b',
    is_active: true,
    sort_order: 1,
    players_per_side: 1,
    default_team_size: 1,
    min_team_size: 1,
    max_team_size: 1,
    default_scoring_format_id: null,
    live_referee_enabled: true,
    referee_profile: 'squash_v1',
    unit_label: null,
    entrant_label: null,
  },
  {
    id: null,
    code: 'padel',
    name: 'Padel',
    icon: 'padel',
    color: '#2743c4',
    is_active: true,
    sort_order: 2,
    players_per_side: 2,
    default_team_size: 2,
    min_team_size: 2,
    max_team_size: 2,
    default_scoring_format_id: null,
    live_referee_enabled: false,
    referee_profile: null,
    unit_label: null,
    entrant_label: null,
  },
];

let current = FALLBACK;
let inflight = null;
const listeners = new Set();

export function loadSports({ force = false } = {}) {
  if (inflight && !force) return inflight;
  inflight = v2Api
    .get('/Sport')
    .then(({ data }) => {
      if (Array.isArray(data) && data.length) {
        current = [...data].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || String(a.name).localeCompare(String(b.name)));
        listeners.forEach((l) => l());
      }
      return current;
    })
    .catch(() => current);
  return inflight;
}

export function getSports() {
  return current;
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Tous les sports (actifs et inactifs), triés. */
export function useSports() {
  return useSyncExternalStore(subscribe, getSports);
}

/** Codes des sports actifs, triés — pour les listes de choix (terrains, tarifs, règles, onglets du planning). */
export function activeSportCodes(sports = current) {
  return sports.filter((s) => s.is_active !== false).map((s) => s.code);
}

export function sportName(code) {
  return current.find((s) => s.code === code)?.name;
}

/**
 * Sports d'une liste déroulante (objets sport, valeur = id) : les sports proposés par le club (actifs) + le sport déjà
 * choisi même s'il a été retiré depuis — jamais tout le catalogue (Configuration → Sports → Ajouter un sport).
 */
export function selectableSports(sports, currentId) {
  return (sports || []).filter((s) => s.is_active !== false || (currentId && String(s.id) === String(currentId)));
}

/** Choix d'un sélecteur : sports actifs + la valeur courante même si son sport a été désactivé depuis. */
export function sportChoices(sports, value) {
  const codes = activeSportCodes(sports);
  return value && !codes.includes(value) ? [...codes, value] : codes;
}

/** Métadonnées d'un sport (catalogue), ou null si inconnu. */
export function getSportInfo(code) {
  return current.find((s) => s.code === code) || null;
}

/** Sport par défaut des formulaires : le premier sport actif du catalogue. */
export function defaultSportCode() {
  return activeSportCodes()[0] || current[0]?.code || 'squash';
}

/** Sport d'équipe (plus d'un joueur par côté) — moteur de tournoi « équipes » (même règle que l'API). */
export function isTeamSportCode(code) {
  const s = getSportInfo(code);
  return Boolean(s && (s.is_team_sport || (s.players_per_side ?? 1) > 1));
}

/** Joueurs d'une partie par défaut : 2 × joueurs par côté (squash 2, padel 4). */
export function defaultPartySize(code) {
  return 2 * Math.max(1, getSportInfo(code)?.players_per_side ?? 1);
}

/**
 * Réglages multi-sport d'un sport (SportDto, docs/MULTISPORT.md), avec repli sur les anciens champs
 * (`players_per_side` / `max_players_per_side`) : tailles d'équipe (joueurs par côté), format de score par défaut,
 * arbitrage live (`referee_profile` : 'squash_v1' | 'rally' | 'tennis' | null) et libellés (null = dérivés).
 */
export function sportSettings(code) {
  const s = getSportInfo(code);
  const defaultTeamSize = Math.max(1, s?.default_team_size ?? s?.players_per_side ?? 1);
  return {
    defaultTeamSize,
    minTeamSize: Math.max(1, s?.min_team_size ?? defaultTeamSize),
    maxTeamSize: Math.max(defaultTeamSize, s?.max_team_size ?? s?.max_players_per_side ?? defaultTeamSize),
    defaultScoringFormatId: s?.default_scoring_format_id ?? null,
    liveRefereeEnabled: Boolean(s?.live_referee_enabled && s?.referee_profile),
    refereeProfile: s?.referee_profile ?? null,
    unitLabel: s?.unit_label || null,
    entrantLabel: s?.entrant_label || null,
  };
}

/** Couleur d'accent du sport (#RRGGBB), ou `fallback` s'il n'en a pas. */
export function sportColor(code, fallback) {
  return getSportInfo(code)?.color || fallback;
}
